import { humanizeZoneLabel } from './proofDisplayUtils';

export type ProofActionType =
  | 'safety_intervention'
  | 'incident_review'
  | 'evidence_confidence'
  | 'diagnostics'
  | 'measurement'
  | 'unknown';

export type ProofActionRoutingContext = {
  orgId?: string;
  siteId?: string;
  windowStart?: string;
  windowEnd?: string;
  hotspot?: string;
  zoneId?: string;
  dominantIncidentType?: string;
  incidentType?: string;
  affectedDevices?: string[];
  affectedStages?: string[];
  evidenceConfidence?: string;
  proofCaseDeviceId?: string;
  proofCaseZoneId?: string;
  proofCaseZoneName?: string;
  proofCaseSiteId?: string;
  proofCaseWindowStart?: string;
  proofCaseWindowEnd?: string;
  proofCaseIncidentType?: string;
  proofReportId?: string;
  proofPackageId?: string;
  fallbackDeviceId?: string;
  source?: string;
  proofSurface?: string;
};

export type ProofActionRoutingCapabilities = {
  canUseCopilot: boolean;
  canUseRunbooks: boolean;
  canReadIncidents: boolean;
  canOpenDiagnostics: boolean;
  canControlDevices: boolean;
  canCreateFollowupReview: boolean;
};

export type RoutedCta = {
  label: string;
  targetSurface:
    | 'copilot'
    | 'runbook'
    | 'incidents'
    | 'diagnostics'
    | 'device_control'
    | 'followup_review'
    | 'evidence_package';
  intent: string;
  href?: string;
  navigationTarget?: string;
  promptSeed?: string;
  runbookId?: string;
  filters?: Record<string, unknown>;
  diagnosticTarget?: Record<string, unknown>;
  enabled: boolean;
  omittedBecause?: string;
};

export type RoutedActionResult = {
  primaryCta: RoutedCta | null;
  secondaryCtas: RoutedCta[];
  routingReason: string;
  matchedRule: string;
};

type RouteInput = {
  actionId?: string;
  actionTitle?: string;
  actionType: ProofActionType;
  context: ProofActionRoutingContext;
  capabilities: ProofActionRoutingCapabilities;
};

type MatchedRule =
  | 'camera_offline'
  | 'clip_evidence_failure'
  | 'diagnostics_pipeline'
  | 'incident_review'
  | 'ambiguous_safety_intervention'
  | 'measurement'
  | 'unknown_fallback';

type CandidateSeed = Omit<RoutedCta, 'enabled'>;

const warnedUnknownActions = new Set<string>();
const warnedProofFallbackRunbooks = new Set<string>();

const CAMERA_RULE_IDS = new Set([
  'camera_offline',
  'fix_offline_camera',
  'restore_camera',
  'camera_recovery',
  'fix_offline_hardware',
]);

const CLIP_RULE_IDS = new Set([
  'restore_clip_capture',
  'enable_clip_capture',
  'missing_required_clips',
  'evidence_recovery',
  'fix_evidence_capture',
  'enable_clip_capture_required_types',
  'restore_high_risk_clips',
]);

const DIAGNOSTICS_RULE_IDS = new Set([
  'fix_degraded_inference',
  'degraded_inference',
  'stabilize_edge_detection',
  'inspect_pipeline_diagnostics',
  'pipeline_health',
  'investigate_degraded_pipeline',
  'reduce_evidence_delay',
]);

const INCIDENT_RULE_IDS = new Set([
  'review_open_zone_violations',
  'review_open_incidents',
  'review_hotspot_incidents',
  'investigate_incident_queue',
  'review_high_risk',
  'strengthen_inconclusive_evidence',
  'separate_demo_activity',
  'review_warnings_before_approval',
]);

const SAFETY_RULE_IDS = new Set([
  'review_zone_controls',
  'review_pedestrian_zone_controls',
  'review_hotspot_zone',
  'zone_hardening_review',
  'safety_intervention',
  'review_hotspot',
]);

const MEASUREMENT_RULE_IDS = new Set([
  'measure_improvement',
  'create_followup_review',
  'schedule_next_comparison',
]);

function normalize(value?: string): string {
  return String(value || '').trim().toLowerCase();
}

function uniqueNonEmpty(values: Array<string | null | undefined>): string[] {
  return Array.from(new Set(values.map((value) => String(value || '').trim()).filter(Boolean)));
}

function firstAffectedDevice(context: ProofActionRoutingContext): string {
  return uniqueNonEmpty(context.affectedDevices || [])[0] || '';
}

function preferredContextDevice(context: ProofActionRoutingContext): string {
  return (
    String(context.proofCaseDeviceId || '').trim()
    || firstAffectedDevice(context)
    || String(context.fallbackDeviceId || '').trim()
  );
}

function preferredContextSite(context: ProofActionRoutingContext): string {
  return String(context.proofCaseSiteId || context.siteId || '').trim();
}

function preferredContextZone(context: ProofActionRoutingContext): string {
  return String(context.proofCaseZoneId || context.zoneId || context.proofCaseZoneName || '').trim();
}

function preferredContextIncidentType(context: ProofActionRoutingContext): string {
  return String(context.proofCaseIncidentType || context.incidentType || '').trim();
}

function preferredContextWindowStart(context: ProofActionRoutingContext): string {
  return String(context.proofCaseWindowStart || context.windowStart || '').trim();
}

function preferredContextWindowEnd(context: ProofActionRoutingContext): string {
  return String(context.proofCaseWindowEnd || context.windowEnd || '').trim();
}

function warnProofRoutingFallback(context: ProofActionRoutingContext, actionId?: string): void {
  const source = String(context.source || '').trim().toLowerCase();
  const fallbackDeviceId = String(context.fallbackDeviceId || '').trim();
  const canonicalProofDeviceId = String(context.proofCaseDeviceId || '').trim();
  if (source !== 'proof' || canonicalProofDeviceId || !fallbackDeviceId) return;
  if (typeof process !== 'undefined' && process.env.NODE_ENV === 'production') return;
  const key = [
    fallbackDeviceId,
    String(actionId || '').trim(),
    String(context.proofReportId || '').trim(),
    String(context.proofPackageId || '').trim(),
  ].join('::');
  if (warnedProofFallbackRunbooks.has(key)) return;
  warnedProofFallbackRunbooks.add(key);
  // eslint-disable-next-line no-console
  console.warn(
    '[Proof routing] Runbook launched without canonical proof device; falling back to filter device',
    {
      filterDeviceId: fallbackDeviceId,
      actionId: String(actionId || '').trim(),
      proofReportId: String(context.proofReportId || '').trim(),
      proofPackageId: String(context.proofPackageId || '').trim(),
    },
  );
}

function buildQueryString(params: Record<string, unknown>): string {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value == null) return;
    const text = String(value).trim();
    if (!text) return;
    search.set(key, text);
  });
  const encoded = search.toString();
  return encoded ? `?${encoded}` : '';
}

function buildIncidentsTarget(context: ProofActionRoutingContext): string {
  const zoneId = String(context.proofCaseZoneId || context.zoneId || '').trim();
  const incidentType = preferredContextIncidentType(context);
  return `/incidents${buildQueryString({
    site: preferredContextSite(context),
    device_id: preferredContextDevice(context),
    zone_id: zoneId,
    incident_type: incidentType,
    from_date: preferredContextWindowStart(context),
    to_date: preferredContextWindowEnd(context),
    tab: 'open',
    table_mode: 'grouped',
    focus: 'queue',
  })}`;
}

function buildDiagnosticsTarget(context: ProofActionRoutingContext): string {
  return `/diagnostics${buildQueryString({
    device: preferredContextDevice(context),
    site: preferredContextSite(context),
  })}`;
}

function buildRunbookTarget(
  context: ProofActionRoutingContext,
  runbookId: string,
  extraParams: Record<string, string | undefined> = {},
  actionId?: string,
): string {
  const deviceId = preferredContextDevice(context);
  const caseId = String(context.proofReportId || '').trim() || undefined;
  warnProofRoutingFallback(context, actionId);
  return `/ops/runbooks${buildQueryString({
    device_id: deviceId,
    zone_id: preferredContextZone(context),
    site_id: preferredContextSite(context),
    incident_type: preferredContextIncidentType(context),
    from_date: preferredContextWindowStart(context),
    to_date: preferredContextWindowEnd(context),
    incident_id: caseId,
    case_id: caseId,
    proof_package_id: String(context.proofPackageId || '').trim() || undefined,
    source: String(context.source || '').trim() || undefined,
    proof_surface: String(context.proofSurface || '').trim() || undefined,
    runbook: runbookId,
    ...extraParams,
  })}`;
}

function buildCopilotTarget(context: ProofActionRoutingContext, promptSeed: string): string {
  return `/ops/copilot${buildQueryString({
    org: context.orgId,
    device: preferredContextDevice(context),
    site: preferredContextSite(context),
    from: preferredContextWindowStart(context),
    to: preferredContextWindowEnd(context),
    evidenceConfidence: context.evidenceConfidence,
    hotspot: context.hotspot,
    dominantType: context.dominantIncidentType,
    source: 'safety_pattern_review',
    autostart: '1',
    prompt: promptSeed,
  })}`;
}

function buildDeviceControlTarget(context: ProofActionRoutingContext): string | undefined {
  const deviceId = preferredContextDevice(context);
  if (!deviceId) return undefined;
  return `/devices/${encodeURIComponent(deviceId)}/control`;
}

function buildZoneHardeningPrompt(actionTitle: string, context: ProofActionRoutingContext): string {
  const hotspot = String(context.hotspot || '').trim();
  const where = hotspot ? ` in ${humanizeZoneLabel(hotspot)}` : '';
  const dominant = String(context.dominantIncidentType || '').trim();
  const windowLabel =
    context.windowStart && context.windowEnd
      ? ` between ${context.windowStart} and ${context.windowEnd}`
      : '';
  return `Help me review ${actionTitle || 'this corrective action'}${where}${windowLabel}. Consider signage, floor markings, boundaries, worker flow, and enforcement${dominant ? ` for ${dominant}` : ''}.`;
}

function buildIncidentPrompt(actionTitle: string, context: ProofActionRoutingContext): string {
  const hotspot = String(context.hotspot || '').trim();
  const dominant = String(context.dominantIncidentType || '').trim();
  return `Help me triage ${actionTitle || 'the incident review work'}${hotspot ? ` around ${humanizeZoneLabel(hotspot)}` : ''}${dominant ? ` for ${dominant}` : ''}. Summarize what should be reviewed first and why.`;
}

function buildDiagnosticsPrompt(actionTitle: string, context: ProofActionRoutingContext): string {
  const stages = uniqueNonEmpty(context.affectedStages || []);
  const stageText = stages.length ? ` Focus on ${stages.join(', ')}.` : '';
  const deviceId = firstAffectedDevice(context);
  return `Help me investigate ${actionTitle || 'this diagnostics issue'}${deviceId ? ` on ${deviceId}` : ''}.${stageText}`;
}

function buildMeasurementPrompt(context: ProofActionRoutingContext): string {
  const hotspot = String(context.hotspot || '').trim();
  const evidenceConfidence = String(context.evidenceConfidence || '').trim();
  return `Help me prepare the next comparison window${hotspot ? ` for ${humanizeZoneLabel(hotspot)}` : ''}.${evidenceConfidence ? ` Current evidence strength is ${evidenceConfidence}.` : ''}`;
}

function candidateEnabled(
  candidate: CandidateSeed,
  capabilities: ProofActionRoutingCapabilities,
): { enabled: boolean; omittedBecause?: string } {
  switch (candidate.targetSurface) {
    case 'copilot':
      return capabilities.canUseCopilot
        ? { enabled: true }
        : { enabled: false, omittedBecause: 'copilot_unavailable' };
    case 'runbook':
      return capabilities.canUseRunbooks
        ? { enabled: true }
        : { enabled: false, omittedBecause: 'runbooks_unavailable' };
    case 'incidents':
      return capabilities.canReadIncidents
        ? { enabled: true }
        : { enabled: false, omittedBecause: 'incidents_unavailable' };
    case 'diagnostics':
      return capabilities.canOpenDiagnostics
        ? { enabled: true }
        : { enabled: false, omittedBecause: 'diagnostics_unavailable' };
    case 'device_control':
      if (!capabilities.canControlDevices) return { enabled: false, omittedBecause: 'device_control_unavailable' };
      return candidate.navigationTarget
        ? { enabled: true }
        : { enabled: false, omittedBecause: 'no_device_target' };
    case 'followup_review':
      return capabilities.canCreateFollowupReview
        ? { enabled: true }
        : { enabled: false, omittedBecause: 'followup_review_unavailable' };
    case 'evidence_package':
      return { enabled: true };
    default:
      return { enabled: false, omittedBecause: 'unknown_surface' };
  }
}

function finalizeCandidates(
  candidates: CandidateSeed[],
  capabilities: ProofActionRoutingCapabilities,
): Pick<RoutedActionResult, 'primaryCta' | 'secondaryCtas'> {
  const visible: RoutedCta[] = [];
  const seenSurfaces = new Set<RoutedCta['targetSurface']>();
  for (const candidate of candidates) {
    const gate = candidateEnabled(candidate, capabilities);
    if (!gate.enabled) continue;
    if (seenSurfaces.has(candidate.targetSurface)) continue;
    seenSurfaces.add(candidate.targetSurface);
    visible.push({ ...candidate, enabled: true });
  }
  return {
    primaryCta: visible[0] || null,
    secondaryCtas: visible.slice(1, 3),
  };
}

function looksEvidenceOrIncidentOriented(actionTitle: string, actionType: ProofActionType): boolean {
  if (actionType === 'incident_review' || actionType === 'evidence_confidence') return true;
  return /(incident|queue|evidence|clip|capture|hotspot|violation|warning|review)/.test(actionTitle);
}

function determineMatchedRule(actionId: string, actionTitle: string, actionType: ProofActionType): MatchedRule {
  const text = `${actionId} ${actionTitle}`;
  // Prefer canonical action IDs before falling back to title heuristics.
  if (CAMERA_RULE_IDS.has(actionId)) {
    return 'camera_offline';
  }
  if (CLIP_RULE_IDS.has(actionId)) {
    return 'clip_evidence_failure';
  }
  if (DIAGNOSTICS_RULE_IDS.has(actionId)) {
    return 'diagnostics_pipeline';
  }
  if (INCIDENT_RULE_IDS.has(actionId)) {
    return 'incident_review';
  }
  if (SAFETY_RULE_IDS.has(actionId)) {
    return 'ambiguous_safety_intervention';
  }
  if (MEASUREMENT_RULE_IDS.has(actionId)) {
    return 'measurement';
  }
  if (/camera offline|offline camera|restore camera|camera recovery|offline hardware/.test(text)) {
    return 'camera_offline';
  }
  if (/clip capture|missing required clips|missing clips|high-risk clips|evidence recovery|evidence capture/.test(text)) {
    return 'clip_evidence_failure';
  }
  if (/degraded inference|pipeline diagnostics|pipeline health|edge detection|diagnostics/.test(text)) {
    return 'diagnostics_pipeline';
  }
  if (
    actionType === 'incident_review'
    || /open incidents|incident queue|review incidents|review unresolved high-risk/.test(text)
  ) {
    return 'incident_review';
  }
  if (
    actionType === 'safety_intervention'
    || /zone controls|pedestrian zone|hotspot zone|zone hardening|safety intervention/.test(text)
  ) {
    return 'ambiguous_safety_intervention';
  }
  if (
    actionType === 'measurement'
    || /follow-up review|next comparison|measure improvement|track improvement/.test(text)
  ) {
    return 'measurement';
  }
  return 'unknown_fallback';
}

function buildCameraRule(actionId: string, actionTitle: string, context: ProofActionRoutingContext): CandidateSeed[] {
  const promptSeed = buildDiagnosticsPrompt(actionTitle, context);
  return [
    {
      label: 'Launch camera recovery runbook',
      targetSurface: 'runbook',
      intent: 'camera_recovery',
      navigationTarget: buildRunbookTarget(context, 'camera_ingest_down', {}, actionId),
      runbookId: 'camera_ingest_down',
      diagnosticTarget: { deviceIds: uniqueNonEmpty(context.affectedDevices || []) },
    },
    {
      label: 'Open diagnostics',
      targetSurface: 'diagnostics',
      intent: 'diagnose_camera_offline',
      navigationTarget: buildDiagnosticsTarget(context),
      diagnosticTarget: { deviceIds: uniqueNonEmpty(context.affectedDevices || []) },
    },
    {
      label: 'Device control',
      targetSurface: 'device_control',
      intent: 'device_remediation',
      navigationTarget: buildDeviceControlTarget(context),
    },
    {
      label: 'Talk through in Copilot',
      targetSurface: 'copilot',
      intent: 'reason_about_camera_recovery',
      navigationTarget: buildCopilotTarget(context, promptSeed),
      promptSeed,
    },
  ];
}

function buildClipRule(actionId: string, actionTitle: string, context: ProofActionRoutingContext): CandidateSeed[] {
  const promptSeed = buildDiagnosticsPrompt(actionTitle, context);
  return [
    {
      label: 'Launch evidence recovery runbook',
      targetSurface: 'runbook',
      intent: 'evidence_recovery',
      navigationTarget: buildRunbookTarget(context, 'incident_evidence_recover', {}, actionId),
      runbookId: 'incident_evidence_recover',
    },
    {
      label: 'Open diagnostics',
      targetSurface: 'diagnostics',
      intent: 'diagnose_evidence_capture',
      navigationTarget: buildDiagnosticsTarget(context),
      diagnosticTarget: { deviceIds: uniqueNonEmpty(context.affectedDevices || []) },
    },
    {
      label: 'Talk through in Copilot',
      targetSurface: 'copilot',
      intent: 'reason_about_evidence_gap',
      navigationTarget: buildCopilotTarget(context, promptSeed),
      promptSeed,
    },
  ];
}

function buildDiagnosticsRule(actionId: string, actionTitle: string, context: ProofActionRoutingContext): CandidateSeed[] {
  const promptSeed = buildDiagnosticsPrompt(actionTitle, context);
  return [
    {
      label: 'Launch diagnostics runbook',
      targetSurface: 'runbook',
      intent: 'diagnostics_recovery',
      navigationTarget: buildRunbookTarget(context, 'camera_ingest_down', {}, actionId),
      runbookId: 'camera_ingest_down',
      diagnosticTarget: { stages: uniqueNonEmpty(context.affectedStages || []) },
    },
    {
      label: 'Open diagnostics',
      targetSurface: 'diagnostics',
      intent: 'inspect_pipeline_health',
      navigationTarget: buildDiagnosticsTarget(context),
      diagnosticTarget: { stages: uniqueNonEmpty(context.affectedStages || []) },
    },
    {
      label: 'Talk through in Copilot',
      targetSurface: 'copilot',
      intent: 'reason_about_diagnostics_issue',
      navigationTarget: buildCopilotTarget(context, promptSeed),
      promptSeed,
    },
  ];
}

function buildIncidentRule(actionId: string, actionTitle: string, context: ProofActionRoutingContext): CandidateSeed[] {
  const promptSeed = buildIncidentPrompt(actionTitle, context);
  return [
    {
      label: 'Open incidents',
      targetSurface: 'incidents',
      intent: 'review_incident_queue',
      navigationTarget: buildIncidentsTarget(context),
      filters: {
        siteId: context.siteId,
        hotspot: context.hotspot,
        dominantIncidentType: context.dominantIncidentType,
        windowStart: context.windowStart,
        windowEnd: context.windowEnd,
      },
    },
    {
      label: 'Run incident review workflow',
      targetSurface: 'runbook',
      intent: 'incident_review_workflow',
      navigationTarget: buildRunbookTarget(context, 'evidence_upload_recover', {}, actionId),
      runbookId: 'evidence_upload_recover',
    },
    {
      label: 'Talk through in Copilot',
      targetSurface: 'copilot',
      intent: 'reason_about_incident_queue',
      navigationTarget: buildCopilotTarget(context, promptSeed),
      promptSeed,
    },
  ];
}

function buildSafetyRule(actionId: string, actionTitle: string, context: ProofActionRoutingContext): CandidateSeed[] {
  const promptSeed = buildZoneHardeningPrompt(actionTitle, context);
  return [
    {
      label: 'Talk through in Copilot',
      targetSurface: 'copilot',
      intent: 'reason_about_safety_intervention',
      navigationTarget: buildCopilotTarget(context, promptSeed),
      promptSeed,
    },
    {
      label: 'Run corrective action workflow',
      targetSurface: 'runbook',
      intent: 'zone_hardening_workflow',
      navigationTarget: buildRunbookTarget(context, 'safety_area_hardening', {
        zone_id: String(context.zoneId || '').trim() || undefined,
        incident_type: String(context.incidentType || '').trim() || undefined,
        from_date: String(context.windowStart || '').trim() || undefined,
        to_date: String(context.windowEnd || '').trim() || undefined,
        focus: 'queue',
        workflow: 'corrective_action',
      }, actionId),
      runbookId: 'safety_area_hardening',
    },
    {
      label: 'View incidents',
      targetSurface: 'incidents',
      intent: 'review_supporting_incidents',
      navigationTarget: buildIncidentsTarget(context),
      filters: {
        siteId: context.siteId,
        hotspot: context.hotspot,
        dominantIncidentType: context.dominantIncidentType,
      },
    },
  ];
}

function buildMeasurementRule(context: ProofActionRoutingContext): CandidateSeed[] {
  const promptSeed = buildMeasurementPrompt(context);
  return [
    {
      label: 'Create follow-up review',
      targetSurface: 'followup_review',
      intent: 'prepare_next_comparison_window',
      navigationTarget: '/acceptance/proof?followup=1',
      filters: {
        siteId: context.siteId,
        windowStart: context.windowStart,
        windowEnd: context.windowEnd,
      },
    },
    {
      label: 'Talk through in Copilot',
      targetSurface: 'copilot',
      intent: 'reason_about_measurement_plan',
      navigationTarget: buildCopilotTarget(context, promptSeed),
      promptSeed,
    },
  ];
}

function buildUnknownRule(
  actionId: string,
  actionTitle: string,
  actionType: ProofActionType,
  context: ProofActionRoutingContext,
  capabilities: ProofActionRoutingCapabilities,
): RoutedActionResult {
  const normalizedTitle = normalize(actionTitle);
  if (actionId && !warnedUnknownActions.has(actionId)) {
    warnedUnknownActions.add(actionId);
    // eslint-disable-next-line no-console
    console.warn(`Unmapped proof action id: ${actionId}`);
  }
  const candidates: CandidateSeed[] = [];
  if (capabilities.canUseCopilot) {
    const promptSeed = buildIncidentPrompt(actionTitle, context);
    candidates.push({
      label: 'Talk through in Copilot',
      targetSurface: 'copilot',
      intent: 'unknown_action_triage',
      navigationTarget: buildCopilotTarget(context, promptSeed),
      promptSeed,
    });
  } else if (looksEvidenceOrIncidentOriented(normalizedTitle, actionType) && capabilities.canReadIncidents) {
    candidates.push({
      label: 'Open incidents',
      targetSurface: 'incidents',
      intent: 'unknown_action_incident_review',
      navigationTarget: buildIncidentsTarget(context),
      filters: {
        siteId: context.siteId,
        hotspot: context.hotspot,
        dominantIncidentType: context.dominantIncidentType,
      },
    });
  }
  const { primaryCta, secondaryCtas } = finalizeCandidates(candidates, capabilities);
  return {
    primaryCta,
    secondaryCtas,
    routingReason: primaryCta
      ? 'This action is not explicitly mapped yet, so it falls back to the next best operational surface.'
      : 'This action is not mapped and no valid operational surface is available for the current capabilities.',
    matchedRule: 'unknown_fallback',
  };
}

export function routeProofAction({
  actionId,
  actionTitle,
  actionType,
  context,
  capabilities,
}: RouteInput): RoutedActionResult {
  const normalizedId = normalize(actionId);
  const normalizedTitle = normalize(actionTitle);
  const matchedRule = determineMatchedRule(normalizedId, normalizedTitle, actionType);

  if (matchedRule === 'unknown_fallback') {
    return buildUnknownRule(normalizedId, normalizedTitle, actionType, context, capabilities);
  }

  const candidates =
    matchedRule === 'camera_offline'
      ? buildCameraRule(normalizedId, actionTitle || actionId || 'camera recovery', context)
      : matchedRule === 'clip_evidence_failure'
        ? buildClipRule(normalizedId, actionTitle || actionId || 'evidence recovery', context)
        : matchedRule === 'diagnostics_pipeline'
          ? buildDiagnosticsRule(normalizedId, actionTitle || actionId || 'diagnostics', context)
          : matchedRule === 'incident_review'
            ? buildIncidentRule(normalizedId, actionTitle || actionId || 'incident review', context)
            : matchedRule === 'ambiguous_safety_intervention'
              ? buildSafetyRule(normalizedId, actionTitle || actionId || 'safety intervention', context)
              : buildMeasurementRule(context);

  const { primaryCta, secondaryCtas } = finalizeCandidates(candidates, capabilities);
  const routingReason =
    matchedRule === 'camera_offline'
      ? 'Camera/offline recovery should go to remediation first, with diagnostics and device control as supporting surfaces.'
      : matchedRule === 'clip_evidence_failure'
        ? 'Evidence capture failures should recover evidence flow first, with diagnostics and guided reasoning as support.'
        : matchedRule === 'diagnostics_pipeline'
          ? 'Concrete diagnostics and pipeline problems should route to runbooks or diagnostics before chat.'
          : matchedRule === 'incident_review'
            ? 'Incident-review work should route to the incident queue first, then structured workflow or Copilot.'
            : matchedRule === 'ambiguous_safety_intervention'
              ? 'Ambiguous safety interventions should start with guided reasoning, then offer workflow execution and evidence review.'
              : 'Measurement stays page-level and offers only a follow-up review path plus optional guided planning.';

  return {
    primaryCta,
    secondaryCtas,
    routingReason,
    matchedRule,
  };
}

export function emitSafetyPatternActionCtaEvent(
  eventName: 'safety_pattern_action_cta_shown' | 'safety_pattern_action_cta_clicked',
  detail: Record<string, unknown>,
): void {
  if (typeof window === 'undefined' || typeof window.dispatchEvent !== 'function') return;
  window.dispatchEvent(new CustomEvent(eventName, { detail }));
}
